import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import EmailIcon from '@mui/icons-material/Email';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../contexts/CartContext';

const Shop = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const { addToCart } = useCart();

  const merchItems = [
    {
      id: 1,
      name: 'Mirmire Scarf',
      description:
        'Show your support with our premium club scarf. Perfect for match days and keeping warm.',
      image: '/merch/scarf.jpg',
      fallbackIcon: '🧣',
      hasSize: false,
      price: 14.99,
    },
    {
      id: 2,
      name: 'Mirmire Jersey',
      description:
        'Official club jersey. High-quality fabric with our iconic colors. Available in various sizes.',
      image: '/merch/jersey.jpeg',
      fallbackIcon: '👕',
      hasSize: true,
      sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
      price: 19.99,
    },
    {
      id: 3,
      name: 'Mirmire Cup',
      description: 'Enjoy your favorite beverage with our official club cup. Durable and stylish.',
      image: '/merch/cup.jpeg',
      fallbackIcon: '☕',
      hasSize: false,
      price: 7.99,
    },
    {
      id: 4,
      name: 'Mirmire Bag',
      description:
        'Carry your essentials in style with our club bag. Perfect for training or everyday use.',
      image: '/merch/bag.jpeg',
      fallbackIcon: '🎒',
      hasSize: false,
      price: 4.99,
    },
  ];

  const handleAddToCartClick = item => {
    setSelectedItem(item);
    setSelectedSize('');
    setQuantity(1);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setSelectedSize('');
    setQuantity(1);
  };

  const handleAddToCart = () => {
    if (selectedItem.hasSize && !selectedSize) {
      setSnackbar({
        open: true,
        message: 'Please select a size',
        severity: 'warning',
      });
      return;
    }

    addToCart(selectedItem, quantity, selectedItem.hasSize ? selectedSize : null);
    setSnackbar({
      open: true,
      message: `${selectedItem.name} added to cart!`,
      severity: 'success',
    });
    handleCloseDialog();
  };

  const handleIncrement = () => {
    setQuantity(prev => prev + 1);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleImageClick = (image, name, itemId) => {
    if (!imageErrors[itemId]) {
      setViewerImage({ src: image, alt: name });
      setImageViewerOpen(true);
    }
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setViewerImage(null);
  };

  return (
    <>
      <Helmet>
        <title>Shop - Mirmire</title>
        <meta
          name="description"
          content="Official Mirmire SC merchandise - Scarves, Jerseys, Cups, and Bags"
        />
      </Helmet>

      {/* Header Section */}
      <Box
        sx={{
          bgcolor: '#000000',
          py: { xs: 6, md: 8 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: '#FFD700',
            fontWeight: 700,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            textAlign: 'center',
          }}
        >
          SHOP
        </Typography>
      </Box>

      {/* Introduction Section */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          py: { xs: 4, md: 6 },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <ShoppingBagIcon sx={{ fontSize: '3rem', color: '#DAA520', mb: 2 }} />
            <Typography
              variant="h4"
              sx={{
                color: '#000000',
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' },
                mb: 2,
              }}
            >
              Official Mirmire Merchandise
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              Support your club with our exclusive merchandise. All items feature our iconic black
              and gold colors. Contact us to place your order!
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Merchandise Grid */}
      <Container maxWidth="lg">
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            py: { xs: 4, md: 6 },
          }}
        >
          <Grid container spacing={4}>
            {merchItems.map(item => (
              <Grid item xs={12} sm={6} md={3} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(218, 165, 32, 0.3)',
                    },
                    border: '1px solid #E0E0E0',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: '#F5F5F5',
                      height: 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: '2px solid #FFD700',
                      overflow: 'hidden',
                      cursor: imageErrors[item.id] ? 'default' : 'pointer',
                      position: 'relative',
                      ...(!imageErrors[item.id] && {
                        '&:hover::after': {
                          content: '"🔍 Click to enlarge"',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          color: '#FFD700',
                          py: 1,
                          textAlign: 'center',
                          fontSize: '0.875rem',
                        },
                      }),
                    }}
                    onClick={() => handleImageClick(item.image, item.name, item.id)}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                        setImageErrors(prev => ({ ...prev, [item.id]: true }));
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '8rem',
                        display: 'none',
                      }}
                    >
                      {item.fallbackIcon}
                    </Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      gutterBottom
                      variant="h5"
                      component="h2"
                      sx={{
                        color: '#000000',
                        fontWeight: 600,
                        fontSize: '1.25rem',
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        mb: 2,
                        flexGrow: 1,
                      }}
                    >
                      {item.description}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#DAA520',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: '1.1rem',
                      }}
                    >
                      €{item.price.toFixed(2)}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleAddToCartClick(item)}
                      startIcon={<AddShoppingCartIcon />}
                      sx={{
                        bgcolor: '#000000',
                        color: '#FFD700',
                        py: 1.5,
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: '#DAA520',
                          color: '#000000',
                        },
                      }}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Contact Information Section */}
      <Box
        sx={{
          bgcolor: '#F5F5F5',
          py: { xs: 6, md: 8 },
          borderTop: '1px solid #E0E0E0',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <EmailIcon sx={{ fontSize: '3rem', color: '#000000', mb: 2 }} />
            <Typography
              variant="h4"
              sx={{
                color: '#000000',
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' },
                mb: 2,
              }}
            >
              Have Questions?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 3,
              }}
            >
              Contact us for pricing, availability, and custom orders
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#DAA520',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              mirmiresc@gmail.com
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Add to Cart Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: '#000000',
            color: '#FFD700',
            fontWeight: 600,
            pb: 2,
            fontSize: '1.5rem',
          }}
        >
          Add to Cart
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Product Preview */}
          <Box
            sx={{
              display: 'flex',
              gap: 2.5,
              mb: 4,
              p: 2.5,
              bgcolor: '#F5F5F5',
              borderRadius: 2,
              border: '1px solid #E0E0E0',
            }}
          >
            {/* Product Image */}
            <Box
              sx={{
                width: 100,
                height: 100,
                bgcolor: '#FFFFFF',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '2px solid #DAA520',
                overflow: 'hidden',
                cursor: imageErrors[selectedItem?.id] ? 'default' : 'pointer',
                ...(!imageErrors[selectedItem?.id] && {
                  '&:hover': {
                    opacity: 0.8,
                    borderColor: '#FFD700',
                  },
                }),
              }}
              onClick={() =>
                handleImageClick(selectedItem?.image, selectedItem?.name, selectedItem?.id)
              }
            >
              <img
                src={selectedItem?.image}
                alt={selectedItem?.name}
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                  setImageErrors(prev => ({ ...prev, [selectedItem?.id]: true }));
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Typography
                sx={{
                  fontSize: '4rem',
                  display: 'none',
                }}
              >
                {selectedItem?.fallbackIcon}
              </Typography>
            </Box>

            {/* Product Info */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#000000', fontSize: '1.3rem' }}
              >
                {selectedItem?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', lineHeight: 1.6 }}>
                {selectedItem?.description}
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: '#DAA520', fontWeight: 700, fontSize: '1rem', mt: 0.5 }}
              >
                €{selectedItem?.price.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Size Selection - Only show for items with sizes */}
          {selectedItem?.hasSize && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1.5, color: '#000000', fontWeight: 600, fontSize: '0.95rem' }}
              >
                Size *
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="size-select-label">Select Size</InputLabel>
                <Select
                  labelId="size-select-label"
                  value={selectedSize}
                  label="Select Size"
                  onChange={e => setSelectedSize(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#DAA520',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FFD700',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FFD700',
                    },
                  }}
                >
                  {selectedItem.sizes.map(size => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Quantity Selection */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#000000', fontWeight: 600, fontSize: '0.95rem' }}
            >
              Quantity
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <IconButton
                onClick={handleDecrement}
                disabled={quantity <= 1}
                sx={{
                  border: '1.5px solid #DAA520',
                  color: '#000000',
                  width: 44,
                  height: 44,
                  '&:hover': {
                    bgcolor: 'rgba(218, 165, 32, 0.1)',
                    borderColor: '#FFD700',
                  },
                  '&:disabled': {
                    borderColor: '#E0E0E0',
                    color: '#999999',
                  },
                }}
              >
                <RemoveIcon />
              </IconButton>
              <Typography
                variant="h5"
                sx={{
                  minWidth: '60px',
                  textAlign: 'center',
                  fontWeight: 700,
                  color: '#000000',
                  fontSize: '1.5rem',
                }}
              >
                {quantity}
              </Typography>
              <IconButton
                onClick={handleIncrement}
                sx={{
                  border: '1.5px solid #DAA520',
                  color: '#000000',
                  width: 44,
                  height: 44,
                  '&:hover': {
                    bgcolor: 'rgba(218, 165, 32, 0.1)',
                    borderColor: '#FFD700',
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Total Price */}
          <Box sx={{ px: 3, py: 2, bgcolor: '#FFF9E6', borderTop: '1px solid #FFD700' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
                Total:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#DAA520' }}>
                €{selectedItem?.price ? (selectedItem.price * quantity).toFixed(2) : '0.00'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 3, gap: 1.5, bgcolor: '#FAFAFA' }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#666666',
              px: 2.5,
              py: 1,
              fontSize: '0.95rem',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddToCart}
            variant="contained"
            startIcon={<AddShoppingCartIcon />}
            sx={{
              bgcolor: '#FFD700',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.95rem',
              '&:hover': {
                bgcolor: '#DAA520',
                color: '#000000',
              },
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={handleCloseImageViewer}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <Box
            sx={{
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#FFFFFF',
              borderRadius: 2,
              border: '3px solid #FFD700',
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleCloseImageViewer}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                color: '#000000',
                bgcolor: '#FFD700',
                '&:hover': {
                  bgcolor: '#DAA520',
                },
                zIndex: 2,
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
            <img
              src={viewerImage?.src}
              alt={viewerImage?.alt}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Shop;
